import React, { useState, useEffect } from 'react';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import { useAuth } from '../../../../context/AuthContext';
import api from '../../../../services/api';
import { formatDateTimeUser, formatRelativeTime, formatDateUser } from '../../utils/dateUtils';
import { getTaskStatusColor, getPriorityColor, getPriorityIcon } from '../../utils/taskUtils';
import { getDistanceLabel } from '../../utils/distanceUtils';
import '../TaskCard/ActionCenter.css';
import { useBodyScrollLock } from '../../hooks/useBodyScrollLock';
import './TaskModals.css';
import 'leaflet/dist/leaflet.css';

const SubtaskModal = ({ 
  selectedSubtask, 
  onClose, 
  departments = [], 
  employees = [], 
  employeeLocations = [],
  onTaskAssignment,
  onImageClick
}) => {
  console.log('selectedSubtask', selectedSubtask);
  selectedSubtask = selectedSubtask?.id;
  const { company, user } = useAuth();
  const [supervisors, setSupervisors] = useState([]);
  const [subtask, setSubtask] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Action Center state
  const [selectedDepartment, setSelectedDepartment] = useState('');
  const [selectedSupervisor, setSelectedSupervisor] = useState('');
  const [selectedEmployee, setSelectedEmployee] = useState('');
  const [assignmentNotes, setAssignmentNotes] = useState('');
  const [filteredEmployees, setFilteredEmployees] = useState([]);
  const [isLoadingSupervisors, setIsLoadingSupervisors] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);

  // Fetch subtask details when modal opens or selectedSubtask changes
  useEffect(() => {
    if (selectedSubtask) {
      fetchSubtaskDetails();
    }
  }, [selectedSubtask]);

  // Initialize action center state when subtask data is loaded
  useEffect(() => {
    if (subtask && !isInitialized) {
      setSelectedDepartment(subtask?.department?.id?.toString() || '');
      setSelectedSupervisor(subtask?.supervisor?.id?.toString() || '');
      setSelectedEmployee(subtask?.assignedEmployee?.id?.toString() || '');
      setAssignmentNotes(subtask?.supervisorNotes || '');
      setIsInitialized(true);
    }
  }, [subtask, isInitialized]);

  // Fetch supervisors when component mounts or company changes
  useEffect(() => {
    if (company?.id) {
      fetchSupervisors();
    }
  }, [company?.id]);

  // Filter employees and supervisors when department changes
  useEffect(() => {
    if (selectedDepartment) {
      const deptEmployees = employees.filter(emp => {
        return emp.departments && emp.departments.some(dept => 
          dept.id === parseInt(selectedDepartment)
        );
      });
      
      setFilteredEmployees(deptEmployees);
      
      if (isInitialized && selectedDepartment !== subtask?.department?.id?.toString()) {
        setSelectedSupervisor('');
        setSelectedEmployee('');
      }
    } else {
      setFilteredEmployees([]);
      if (isInitialized) {
        setSelectedSupervisor('');
        setSelectedEmployee('');
      }
    }
  }, [selectedDepartment, employees, supervisors, isInitialized, subtask?.department?.id]);

  const fetchSubtaskDetails = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await api.get(`/api/tasks/${selectedSubtask}`);
      
      if (response.data?.is_success) {
        setSubtask(response.data.task || response.data.complaint);
      } else {
        setError(response.data?.message || 'Failed to load subtask details');
      }
    } catch (err) {
      console.error('Error fetching subtask details:', err);
      setError('Failed to load subtask details. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const fetchSupervisors = async () => {
    setIsLoadingSupervisors(true);
    try {
      const params = user?.role === 'supervisor' ? { supervisorId: user.id } : {};
      const response = await api.get('/api/companies/users', { params });
      if (response.data?.is_success) {
        const supervisorList = response.data.users.filter(user => 
          (user.role === 'admin' || user.role === 'supervisor') && user.isActive
        );
        setSupervisors(supervisorList);
      }
    } catch (error) {
      console.error('Error fetching supervisors:', error);
    } finally {
      setIsLoadingSupervisors(false);
    }
  };

  const getFilteredSupervisors = () => {
    if (!selectedDepartment) return [];
    
    return supervisors.filter(sup => {
      if (sup.role === 'admin') {
        return true;
      }
      return sup.departments && sup.departments.some(dept => 
        dept.id === parseInt(selectedDepartment)
      );
    });
  };

  const handleActionCenterSubmit = async (e) => {
    e.preventDefault();
    
    if (!selectedDepartment) {
      alert('Please select a department');
      return;
    }

    setSubmitting(true);
    
    try {
      const assignmentData = {
        departmentId: parseInt(selectedDepartment),
        supervisorAssigned: selectedSupervisor && selectedSupervisor.trim() !== '' ? parseInt(selectedSupervisor) : null,
        employeeAssigned: selectedEmployee && selectedEmployee.trim() !== '' ? parseInt(selectedEmployee) : null,
        supervisorNotes: assignmentNotes.trim(),
        status: (selectedSupervisor && selectedSupervisor.trim() !== '') || (selectedEmployee && selectedEmployee.trim() !== '') ? 'pending' : 'incoming'
      };

      console.log('Assignment payload:', assignmentData); // Debug log

      if (onTaskAssignment) {
        await onTaskAssignment(subtask.id, assignmentData, subtask.parentComplaintId || subtask.parentComplaint?.id);
        // Refresh subtask details after assignment
        await fetchSubtaskDetails();
      }
      
    } catch (error) {
      console.error('Error assigning subtask:', error);
      alert('Failed to assign subtask. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  // === New: Resolve & Reject handlers ===
  const handleResolve = async () => {
    if (!subtask || !hasDepartmentPermission) return;
    try {
      if (onTaskAssignment) {
        await onTaskAssignment(subtask.id, { status: 'resolved' }, subtask.parentComplaintId || subtask.parentComplaint?.id);
        await fetchSubtaskDetails();
      }
    } catch (error) {
      console.error('Error resolving subtask:', error);
      alert('Failed to resolve subtask. Please try again.');
    }
  };

  const handleReject = async () => {
    if (!subtask || !hasDepartmentPermission) return;
    const reason = window.prompt('Enter a reason for rejection (optional):', '');
    try {
      if (onTaskAssignment) {
        const payload = { status: 'rejected' };
        if (reason && reason.trim() !== '') {
          payload.supervisorNotes = reason.trim();
        }
        await onTaskAssignment(subtask.id, payload, subtask.parentComplaintId || subtask.parentComplaint?.id);
        await fetchSubtaskDetails();
      }
    } catch (error) {
      console.error('Error rejecting subtask:', error);
      alert('Failed to reject subtask. Please try again.');
    }
  };

  // Extract user's department IDs, preferring supervisor object for accurate department list
  const getUserDepartmentIds = () => {
    if (!user) return [];

    // Check supervisors array for the logged-in supervisor record
    if (Array.isArray(supervisors) && supervisors.length > 0) {
      const me = supervisors.find((sup) => sup.id === user.id);
      if (me && Array.isArray(me.departments) && me.departments.length > 0) {
        return me.departments.map((d) => (typeof d === 'object' ? d.id ?? d : d));
      }
    }

    // Fallbacks
    if (Array.isArray(user.departments) && user.departments.length > 0) {
      return user.departments.map((d) => (typeof d === 'object' ? d.id ?? d : d));
    }
    if (user.departmentId) return [user.departmentId];
    if (user.department_id) return [user.department_id];
    if (user.department) {
      if (typeof user.department === 'object' && user.department.id) return [user.department.id];
      return [user.department];
    }
    return [];
  };

  // Permission: allow if admin or belongs to same department (or no department yet)
  const userDeptIds = getUserDepartmentIds();
  const hasDepartmentPermission = user?.role === 'admin' || !subtask?.department?.id || userDeptIds.includes(subtask?.department?.id);

  // Check if subtask can show action center (not resolved/rejected) and user has permission
  const canShowActionCenter = subtask && hasDepartmentPermission && !['resolved', 'rejected'].includes(subtask.status) && onTaskAssignment;

  // Lock body scroll when modal is open
  useBodyScrollLock(!!selectedSubtask);

  // Close on ESC
  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.key === 'Escape') {
        onClose && onClose();
      }
    };
    if (selectedSubtask) {
      window.addEventListener('keydown', onKeyDown);
    }
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [selectedSubtask, onClose]);

  if (!selectedSubtask) return null;

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) onClose && onClose();
  };

  // Build concise header chips similar to Task Details modal
  const headerMetaChips = subtask ? [
    { label: 'Customer', value: subtask.customer?.name || 'N/A' },
    { label: 'Department', value: subtask.department?.name || 'Not assigned' },
    { label: 'Created', value: formatRelativeTime(subtask.createdAt) },
    subtask.dueDate ? { label: 'Due Date', value: formatDateUser(subtask.dueDate) } : null,
    subtask.followUpOn ? { label: 'Follow Up', value: formatDateTimeUser(subtask.followUpOn) } : null
  ].filter(Boolean) : [];

  return (
    <div className="modal-overlay task-modal-overlay subtask-modal-overlay" onClick={handleOverlayClick}>
      <div className="modal-content subtask-modal xlarge task-modal-container" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header task-modal-header">
          <div className="task-modal-header-content">
            <div className="task-title-row">
              <span className="task-id-pill">Subtask #{selectedSubtask}</span>
            </div>
            {!!headerMetaChips.length && (
              <div className="task-meta-row">
                {headerMetaChips.map((chip) => (
                  <div key={chip.label} className="task-meta-chip">
                    <span className="chip-label">{chip.label}</span>
                    <span className="chip-value">{chip.value}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
          <button className="close-button" onClick={onClose} aria-label="Close" title="Close">Close</button>
        </div>

        <div className="modal-body task-modal-body">
          {loading && (
            <div className="loading-state">
              <div className="loading-spinner"></div>
              <p>Loading subtask details...</p>
            </div>
          )}

          {error && (
            <div className="error-state">
              <div className="error-message">{error}</div>
              <button 
                className="btn btn-primary" 
                onClick={fetchSubtaskDetails}
                disabled={loading}
              >
                {loading ? 'Retrying...' : 'Retry'}
              </button>
            </div>
          )}

          {subtask && !loading && !error && (
            <div className="task-details-grid">
              {/* Subtask Header */}
              <div className="subtask-detail-header">
                <div className="task-badges">
                  <span 
                    className="task-status" 
                    style={{ backgroundColor: getTaskStatusColor(subtask.status) }}
                    aria-label={`Status: ${subtask.status.replace('-', ' ')}`}
                  >
                    {subtask.status.replace('-', ' ')}
                  </span>
                  {subtask.priority && (
                    <span 
                      className={`priority-badge priority-${subtask.priority.toLowerCase()}`}
                      aria-label={`Priority: ${subtask.priority}`}
                    >
                      {getPriorityIcon(subtask.priority)} {subtask.priority}
                    </span>
                  )}
                  {subtask.taskType && (
                    <span className="task-type-badge">
                      {subtask.taskType}
                    </span>
                  )}
                  <span className="detail-value meta-value department-name">
                          {subtask.department?.name || 'Not assigned'}
                  </span>

                  {/* Action buttons moved to footer for consistent design */}
                </div>
              
              </div>

  {/* Progress Bar */}
  {subtask.completionPercentage > 0 && (
               <div className="completion-progress">
                    <div className="progress-bar" role="progressbar" aria-valuenow={subtask.completionPercentage} aria-valuemin="0" aria-valuemax="100">
                      <div 
                        className="progress-fill" 
                        style={{ width: `${subtask.completionPercentage}%` }}
                      />
                    </div>
                    <span className="progress-text">{subtask.completionPercentage}% Complete</span>
                  </div>
              )}
              {/* Basic Information and Assignment - Side by Side Row */}
              <div className="info-assignment-row">
                
                {/* Parent Complaint Information */}
                {subtask.parentComplaint && (
                  <div className="detail-section">
                    <h4 className="section-title">Parent Task Details</h4>
                    <div className="detail-rows">
                      <div className="detail-row">
                        {subtask.parentComplaintId && (
                          <div className="detail-item">
                            <span className="detail-label">Parent Task ID:</span>
                            <span className="detail-value">#{subtask.parentComplaintId}</span>
                          </div>
                        )}
                        <div className="detail-item">
                          <span className="detail-label">Status:</span>
                          <span className="detail-value" style={{ 
                            backgroundColor: getTaskStatusColor(subtask.parentComplaint.status),
                            color: 'white',
                            padding: '2px 6px',
                            borderRadius: '4px',
                            fontSize: '10px',
                            fontWeight: '600',
                            textTransform: 'uppercase'
                          }}>
                            {subtask.parentComplaint.status}
                          </span>
                        </div>
                        <div className="detail-item">
                          <span className="detail-label">Priority:</span>
                          <span className={`detail-value priority-badge priority-${subtask.parentComplaint.priority?.toLowerCase()}`}>
                            {getPriorityIcon(subtask.parentComplaint.priority)} {subtask.parentComplaint.priority}
                          </span>
                        </div>
                      </div>
                      <div className="detail-row">
                        <div className="detail-item">
                          <span className="detail-label">Type:</span>
                          <span className="detail-value">{subtask.parentComplaint.taskType}</span>
                        </div>
                        <div className="detail-item">
                          <span className="detail-label">Progress:</span>
                          <span className="detail-value">{subtask.parentComplaint.completionPercentage}%</span>
                        </div>
                        {subtask.parentComplaint.supervisor && (
                          <div className="detail-item">
                            <span className="detail-label">Supervisor:</span>
                            <span className="detail-value">
                              {subtask.parentComplaint.supervisor.firstName} {subtask.parentComplaint.supervisor.lastName}
                            </span>
                          </div>
                        )}
                      </div>
                      <div className="detail-row">
                        <div className="detail-item full-width">
                          <span className="detail-label">Description:</span>
                          <span className="detail-value" >
                            {subtask.parentComplaint.description}
                          </span>
                        </div>
                      </div>
                      {subtask.parentComplaint.dueDate && (
                        <div className="detail-row">
                          <div className="detail-item">
                            <span className="detail-label">Due Date:</span>
                            <span className="detail-value meta-value due-date">
                              {formatDateTimeUser(subtask.parentComplaint.dueDate)}
                            </span>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Customer Information */}
                {(subtask.customer) ? (
                  <div className="detail-section">
                    <h4 className="section-title">Customer Information</h4>
                    <div className="detail-rows">
                      <div className="detail-row">
                        <div className="detail-item">
                          <span className="detail-label">Name:</span>
                          <span className="detail-value meta-value customer-name">{subtask.customer.name}</span>
                        </div>
                        <div className="detail-item">
                          <span className="detail-label">Email:</span>
                          <span className="detail-value">{subtask.customer.email}</span>
                        </div>
                        <div className="detail-item">
                          <span className="detail-label">Phone:</span>
                          <span className="detail-value">{subtask.customer.phone}</span>
                        </div>
                      </div>
                    
                    </div>
                  </div>
                ):(
                  <div className="detail-section">
                  <h4 className="section-title">Customer Information</h4>
                  <div className="detail-rows">
                    <div className="detail-row">
                      <div className="detail-item">
                        <span className="detail-label">Name:</span>
                        <span className="detail-value meta-value customer-name">No Customer</span>
                      </div>
                      
                    </div>
                  
                  </div>
                </div>
                )}
              </div>

              {/* Timeline and Assignment Row */}
              <div className="info-assignment-row">
                {/* Assignment Information */}
                {(subtask.supervisor || subtask.assignedEmployee || subtask.supervisorAssigned || subtask.employeeAssigned) && (
                  <div className="detail-section">
                    <h4 className="section-title">Assignment</h4>
                    <div className="detail-rows">
                      <div className="detail-row">
                        {(subtask.supervisor || subtask.supervisorAssigned) && (
                          <div className="detail-item">
                            <span className="detail-label">Supervisor:</span>
                            <span className="detail-value" style={{ fontWeight: '600' }}>
                              {subtask.supervisor 
                                ? `${subtask.supervisor.firstName} ${subtask.supervisor.lastName}`
                                : `ID: ${subtask.supervisorAssigned}`
                              }
                            </span>
                          </div>
                        )}
                        {(subtask.assignedEmployee || subtask.employeeAssigned) && (
                          <div className="detail-item">
                            <span className="detail-label">Assigned Employee:</span>
                            <span className="detail-value" style={{ fontWeight: '600' }}>
                              {subtask.assignedEmployee 
                                ? `${subtask.assignedEmployee.firstName} ${subtask.assignedEmployee.lastName}`
                                : subtask.employeeAssigned ? `ID: ${subtask.employeeAssigned}` : 'Not assigned'
                              }
                            </span>
                          </div>
                        )}
                      </div>
                      {(subtask.supervisor?.email || subtask.assignedEmployee?.email) && (
                        <div className="detail-row">
                          {subtask.supervisor?.email && (
                            <div className="detail-item">
                              <span className="detail-label">Supervisor Email:</span>
                              <span className="detail-value">{subtask.supervisor.email}</span>
                            </div>
                          )}
                          {subtask.assignedEmployee?.email && (
                            <div className="detail-item">
                              <span className="detail-label">Employee Email:</span>
                              <span className="detail-value">{subtask.assignedEmployee.email}</span>
                            </div>
                          )}
                        </div>
                      )}
                      {/* Notes section integrated here */}
                      {(subtask.supervisorNotes || subtask.employeeNotes || subtask.notes) && (
                        <>
                          {subtask.supervisorNotes && (
                            <div className="detail-row" style={{ marginTop: '12px', paddingTop: '12px', borderTop: '1px solid #e5e7eb' }}>
                              <div className="detail-item full-width">
                                <span className="detail-label">Supervisor Notes:</span>
                                <span className="detail-value" >{subtask.supervisorNotes}</span>
                              </div>
                            </div>
                          )}
                          {subtask.employeeNotes && (
                            <div className="detail-row">
                              <div className="detail-item full-width">
                                <span className="detail-label">Employee Notes:</span>
                                <span className="detail-value" >{subtask.employeeNotes}</span>
                              </div>
                            </div>
                          )}
                          {subtask.notes && (
                            <div className="detail-row">
                              <div className="detail-item full-width">
                                <span className="detail-label">General Notes:</span>
                                <span className="detail-value">{subtask.notes}</span>
                              </div>
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                )}
               {/* Timeline */}
               <div className="detail-section">
                  <h4 className="section-title">Timeline</h4>
                  <div className="detail-rows">
                    <div className="detail-row">
                      {subtask.createdAt && (
                        <div className="detail-item">
                          <span className="detail-label">Created:</span>
                          <span className="detail-value meta-value created-time">
                            {formatDateTimeUser(subtask.createdAt)}
                          </span>
                        </div>
                      )}
                      {subtask.dueDate && (
                        <div className="detail-item">
                          <span className="detail-label">Due date:</span>
                          <span className="detail-value meta-value due-date">
                            {formatDateTimeUser(subtask.dueDate)}
                          </span>
                        </div>
                      )}
                      {subtask.followUpOn && (
                        <div className="detail-item">
                          <span className="detail-label">Follow up:</span>
                          <span className="detail-value meta-value followup-date">
                            {formatDateTimeUser(subtask.followUpOn)}
                          </span>
                        </div>
                      )}
                      {subtask.startedAt && (
                        <div className="detail-item">
                          <span className="detail-label">Started:</span>
                          <span className="detail-value">{formatDateTimeUser(subtask.startedAt)}</span>
                        </div>
                      )}
                    </div>
                    <div className="detail-row">
                      {subtask.supervisorAssignedAt && (
                        <div className="detail-item">
                          <span className="detail-label">Supervisor assigned:</span>
                          <span className="detail-value">{formatDateTimeUser(subtask.supervisorAssignedAt)}</span>
                        </div>
                      )}
                      {subtask.employeeAssignedAt && (
                        <div className="detail-item">
                          <span className="detail-label">Employee assigned:</span>
                          <span className="detail-value">{formatDateTimeUser(subtask.employeeAssignedAt)}</span>
                        </div>
                      )}
                      {subtask.updatedAt && (
                        <div className="detail-item">
                          <span className="detail-label">Last updated:</span>
                          <span className="detail-value">{formatDateTimeUser(subtask.updatedAt)}</span>
                        </div>
                      )}
                    </div>
                    {subtask.resolvedAt && (
                      <div className="detail-row">
                        <div className="detail-item full-width">
                          <span className="detail-label">✅ Resolved:</span>
                          <span className="detail-value" style={{
                            backgroundColor: '#dcfce7',
                            color: '#166534',
                            padding: '4px 8px',
                            borderRadius: '4px',
                            fontWeight: '600'
                          }}>{formatDateTimeUser(subtask.resolvedAt)}</span>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                
              </div>

              {/* Description and Location Row */}
              <div className="info-assignment-row">
                
                

 {/* Description */}
 <div className="detail-section">
                  <h4 className="section-title">Description</h4>
                  <div className="detail-rows">
                    <div className="detail-row">
                      <div className="detail-item full-width">
                        <span className="detail-value" >{subtask.description}</span>
                      </div>
                    </div>
                    {subtask.images && subtask.images.length > 0 && (
                    <div className="images-subsection">
                      <div className="subsection-title">Task Images</div>
                      <div className="task-images-grid">
                        {subtask.images.map((image, index) => (
                          <div key={index} className="image-container">
                            <img 
                              src={image} 
                              alt={`Task image ${index + 1}`} 
                              className="task-image"
                              onClick={() => onImageClick ? onImageClick(image) : window.open(image, '_blank')}
                            />
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  {subtask.employeeimages && subtask.employeeimages.length > 0 && (
                    <div className="images-subsection">
                      <div className="subsection-title">Employee Images</div>
                      <div className="task-images-grid">
                        {subtask.employeeimages.map((image, index) => (
                          <div key={index} className="image-container">
                            <img 
                              src={image} 
                              alt={`Employee image ${index + 1}`} 
                              className="task-image"
                              onClick={() => onImageClick ? onImageClick(image) : window.open(image, '_blank')}
                            />
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  </div>
                </div>
                {/* Location Information with Map */}
                {subtask.location && (
                  <div className="detail-section">
                    <h4 className="section-title">Location</h4>
                    <div className="detail-rows">
                      <div className="detail-row">
                        <div className="detail-item full-width">
                          <span className="detail-label">Address:</span>
                          <span className="detail-value">{subtask.location.address}</span>
                        </div>
                      </div>
                      {subtask.location.coordinates && (
                        <div className="detail-row">
                          <div className="detail-item">
                            <span className="detail-label">Latitude:</span>
                            <span className="detail-value">{subtask.location.coordinates.lat}</span>
                          </div>
                          <div className="detail-item">
                            <span className="detail-label">Longitude:</span>
                            <span className="detail-value">{subtask.location.coordinates.lng}</span>
                          </div>
                        </div>
                      )}
                    </div>
                    
                    {/* Map View */}
                    {subtask.location.coordinates?.lat && subtask.location.coordinates?.lng && (
                      <div className="location-map" style={{ marginTop: '12px', height: '200px', borderRadius: '8px', overflow: 'hidden' }}>
                        <MapContainer 
                          center={[subtask.location.coordinates.lat, subtask.location.coordinates.lng]} 
                          zoom={15} 
                          style={{ height: '100%', width: '100%' }}
                          scrollWheelZoom={false}
                          dragging={true}
                          zoomControl={true}
                        >
                          <TileLayer
                            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                          />
                          <Marker position={[subtask.location.coordinates.lat, subtask.location.coordinates.lng]}>
                            <Popup>
                              <div>
                                <strong>Subtask Location</strong><br/>
                                {subtask.location.address}
                              </div>
                            </Popup>
                          </Marker>
                        </MapContainer>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Action Center - Show if subtask is not resolved or rejected */}
              {canShowActionCenter && (
                <div className="action-center" onClick={(e) => e.stopPropagation()}>
                  <div className="action-center-header">
                    <h4 className="section-title">Action Center</h4>
                    <p className="action-center-description">
                      Modify the team assignment and optionally reassign supervisor and employee for this subtask
                    </p>
                  </div>

                  <form onSubmit={handleActionCenterSubmit} className="action-center-form">
                    <div className="form-row">
                      <div className="form-group">
                        <label htmlFor="department" className="form-label">Team *</label>
                        <select
                          id="department"
                          value={selectedDepartment}
                          onChange={(e) => setSelectedDepartment(e.target.value)}
                          onClick={(e) => e.stopPropagation()}
                          className="form-select"
                          required
                          disabled={submitting}
                        >
                          <option value="">Select Team</option>
                          {departments.map(dept => (
                            <option key={dept.id} value={dept.id}>
                              {dept.name}
                            </option>
                          ))}
                        </select>
                      </div>

                      {selectedDepartment && (
                        <div className="form-group">
                          <label htmlFor="supervisor" className="form-label">Supervisor</label>
                          <select
                            id="supervisor"
                            value={selectedSupervisor}
                            onChange={(e) => setSelectedSupervisor(e.target.value)}
                            onClick={(e) => e.stopPropagation()}
                            className="form-select"
                            disabled={submitting || isLoadingSupervisors}
                          >
                            <option value="">Select Supervisor (Optional)</option>
                            {getFilteredSupervisors().map(supervisor => (
                              <option key={supervisor.id} value={supervisor.id}>
                                {supervisor.firstName} {supervisor.lastName}
                                {supervisor.role === 'admin' ? ' (Admin)' : ''}
                              </option>
                            ))}
                          </select>
                        </div>
                      )}
                    </div>

                    {selectedDepartment && (
                      <div className="form-group">
                        <label htmlFor="employee" className="form-label">Field Staff</label>
                        <select
                          id="employee"
                          value={selectedEmployee}
                          onChange={(e) => setSelectedEmployee(e.target.value)}
                          onClick={(e) => e.stopPropagation()}
                          className="form-select"
                          disabled={submitting}
                        >
                          <option value="">Select Field Staff (Optional)</option>
                          {filteredEmployees.map(employee => (
                            <option key={employee.id} value={employee.id}>
                              {employee.firstName} {employee.lastName} {getDistanceLabel(employee, subtask?.location, employeeLocations)}
                            </option>
                          ))}
                        </select>
                      </div>
                    )}

                    <div className="form-group">
                      <label htmlFor="notes" className="form-label">Assignment Notes</label>
                      <textarea
                        id="notes"
                        value={assignmentNotes}
                        onChange={(e) => setAssignmentNotes(e.target.value)}
                        onClick={(e) => e.stopPropagation()}
                        className="form-textarea"
                        rows="3"
                        placeholder="Add any special instructions or notes for the assignment..."
                        disabled={submitting}
                      />
                    </div>
                    
                    {selectedEmployee && (
                      <div className="assignment-preview">
                        <span className="preview-text">
                          Will assign to: {filteredEmployees.find(emp => emp.id === parseInt(selectedEmployee))?.firstName} {filteredEmployees.find(emp => emp.id === parseInt(selectedEmployee))?.lastName}
                        </span>
                      </div>
                    )}

                    <div className="form-actions">
                      <button 
                        type="submit" 
                        className="btn btn-primary"
                        disabled={submitting || !selectedDepartment}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleActionCenterSubmit(e);
                        }}
                      >
                        {submitting ? (
                          <>
                            <div className="button-spinner"></div>
                            Updating...
                          </>
                        ) : (
                          'Update Assignment'
                        )}
                      </button>
                    </div>
                  </form>
                </div>
              )}

              
            </div>
          )}
        </div>

        <div className="modal-footer task-modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>Close</button>
          {hasDepartmentPermission && subtask && !['resolved', 'rejected'].includes(subtask.status) && (
            <>
              <button 
                className="action-button resolve" 
                onClick={(e) => { e.stopPropagation(); handleResolve(); }}
              >
                Resolve
              </button>
              <button 
                className="action-button reject" 
                onClick={(e) => { e.stopPropagation(); handleReject(); }}
              >
                Reject
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default SubtaskModal; 